import { useRef } from 'react';
import { useFormikContext, getIn } from 'formik';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const PLACEHOLDERS = [
  { key: 'first_name', label: "Customer's first name" },
  { key: 'last_name', label: "Customer's last name" },
  { key: 'full_name', label: "Customer's full name" },
  { key: 'stylist_name', label: 'Stylist name' },
  { key: 'event_title', label: 'Event title, e.g. "Wash & Set"' },
  { key: 'date', label: 'Event date' },
  { key: 'start_time', label: 'Start time' },
  { key: 'end_time', label: 'End time' },
  { key: 'location', label: 'Your business location' },
];

const MESSAGES = [
  {
    key: 'dropoff_msg',
    disableKey: 'disable_dropoff_msg',
    title: 'Wig Dropoff Reminder Message',
    switchLabel: 'Send wig dropoff reminder',
    description: 'Sent to remind the customer to drop off their wig.',
  },
  {
    key: 'appt_msg',
    disableKey: 'disable_appt_reminder_msg',
    title: 'Appointment Reminder Message',
    switchLabel: 'Send appointment reminder',
    description: 'Sent to the customer before their scheduled appointment.',
  },
];

const MessageEditor = ({ config, disabled }) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext();
  const inputRef = useRef(null);

  const name = `settings.${config.key}`;
  const value = getIn(values, name) ?? '';
  const error = getIn(touched, name) && getIn(errors, name);
  const hasCustom = value.trim().length > 0;

  const insertPlaceholder = (token) => {
    const el = inputRef.current;
    const text = `{{${token}}}`;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);

    setFieldValue(name, next);

    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + text.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Accordion disableGutters variant="outlined" disabled={disabled} sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
          <Typography variant="subtitle2">{config.title}</Typography>
          <Chip
            size="small"
            label={disabled ? 'Not sent' : hasCustom ? 'Custom' : 'Using default'}
            color={hasCustom && !disabled ? 'primary' : 'default'}
            variant={hasCustom && !disabled ? 'filled' : 'outlined'}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {config.description} Leave empty to use the default message from your general SMS settings.
        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Click a placeholder to insert it at the cursor:
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
          {PLACEHOLDERS.map((p) => (
            <Chip
              key={p.key}
              size="small"
              variant="outlined"
              label={`{{${p.key}}}`}
              title={p.label}
              onClick={() => insertPlaceholder(p.key)}
              sx={{ fontFamily: 'monospace' }}
            />
          ))}
        </Stack>

        <TextField
          inputRef={inputRef}
          name={name}
          value={value}
          onChange={(e) => setFieldValue(name, e.target.value)}
          onBlur={() => setFieldTouched(name, true)}
          placeholder="Leave empty to send the default message"
          multiline
          minRows={3}
          fullWidth
          error={Boolean(error)}
          helperText={error || ' '}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const ServiceMessageSettings = () => {
  const { values, setFieldValue } = useFormikContext();
  const settings = values.settings ?? {};

  return (
    <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Typography variant="subtitle1">SMS Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Control which reminders are sent for this service, and optionally override the default messages.
      </Typography>

      <Stack sx={{ mb: 2 }}>
        {MESSAGES.map((m) => (
          <FormControlLabel
            key={m.disableKey}
            label={m.switchLabel}
            control={
              <Switch
                checked={!settings[m.disableKey]}
                onChange={(e) => setFieldValue(`settings.${m.disableKey}`, !e.target.checked)}
              />
            }
          />
        ))}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Box>
        <Stack spacing={1}>
          {MESSAGES.map((m) => (
            <MessageEditor key={m.key} config={m} disabled={Boolean(settings[m.disableKey])} />
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};

export default ServiceMessageSettings;